'use client';

import { GetProfessionalsAdminStatusFilter } from '@api/models';
import { useQuery } from '@tanstack/react-query';
import { useLocale } from 'next-intl';

import { ProfessionalAdmin, getProfessionalsAdmin } from '../services/professionals.service';

interface UseProfessionalsAdminPaginatedFilters {
  statusFilter?: GetProfessionalsAdminStatusFilter;
  query?: string;
  /**
   * When `statusFilter=pending` and this is true, the backend expands the pending
   * bucket to include `incomplete` mentors (profiles still being finished). Admins
   * can flip this to triage stalled onboardings.
   */
  includeIncomplete?: boolean;
  /**
   * Filter by group membership. Empty/undefined = no filter.
   * Include `__none__` (`NO_GROUPS_TOKEN`) to match ungrouped professionals.
   */
  groupIds?: string[];
}

/**
 * Hook for admin professionals list.
 * Fetches ALL professionals across all pages for client-side filtering.
 *
 * Note: For admin use cases with < 1000 professionals, fetching all is acceptable.
 *
 * @example
 * ```tsx
 * const { data, isLoading } = useProfessionalsAdminPaginated();
 * // Filter client-side based on showResolved toggle and search query
 * ```
 */
export function useProfessionalsAdminPaginated(filters: UseProfessionalsAdminPaginatedFilters = {}) {
  const locale = useLocale();

  const groupIdsKey = (filters.groupIds ?? []).slice().sort().join('|');
  return useQuery<ProfessionalAdmin[]>({
    queryKey: [
      'professionals-admin',
      locale,
      filters.statusFilter,
      filters.query,
      filters.includeIncomplete ?? false,
      groupIdsKey
    ],
    queryFn: async () => {
      const allProfessionals: ProfessionalAdmin[] = [];
      let cursor: string | undefined = undefined;

      do {
        const result = await getProfessionalsAdmin({
          locale,
          query: filters.query,
          statusFilter: filters.statusFilter,
          includeIncomplete: filters.includeIncomplete,
          groupIds: filters.groupIds && filters.groupIds.length > 0 ? filters.groupIds : undefined,
          limit: 100,
          cursor
        });
        allProfessionals.push(...result.professionals);
        cursor = result.nextCursor;
      } while (cursor);

      return allProfessionals;
    },
    staleTime: 60 * 1000
  });
}
